/**
 * The design's type sizes, as class strings.
 *
 * The app's theme runs a larger scale than the marketing design does: its
 * `text-xl` is 24px and its `text-base` 18px, where the design sets body copy at
 * 20px and supporting copy at 16px. Changing the theme would resize the whole
 * product, so the marketing pages name the sizes they mean instead, and pass
 * them to the shared Type component, which lets a later class win. Each size
 * is stated at the `md` breakpoint as well, because several Type variants set
 * their size there and an unprefixed class would lose to it.
 */
export const marketingType = {
  /** Paragraphs: 16px on a phone, 20px on a 30px line from a tablet up */
  body: "text-[16px] leading-[24px] md:text-[16px] lg:text-[20px] md:leading-[24px] lg:leading-[30px]",
  /** Supporting copy, roles, list rows: 16px on a 24px line */
  small: "text-[16px] leading-[24px] md:text-[16px]",
  /** Attributions under a band quote: 14px on a phone, 16px up */
  caption: "text-[14px] leading-[20px] md:text-[14px] lg:text-[16px] md:leading-[20px] lg:leading-[24px]",
  /** The label above a section: 12px bold capitals on a phone, 16px up */
  eyebrow:
    "text-[12px] leading-[20px] md:text-[12px] lg:text-[16px] md:leading-[20px] lg:leading-[24px] font-bold uppercase",
  /** The label inside a card or panel: 12px bold capitals */
  cardEyebrow: "text-[12px] leading-[20px] md:text-[12px] font-bold uppercase",
  /**
   * Statements and card headings: 20px on a phone, 36px on a 45px line up.
   *
   * The letter spacing is the design's for a bold heading: -0.4px at 20px and
   * -0.36px at 36px. It is stated at every width because the shared heading
   * style underneath has a tighter one that would otherwise show through on a
   * phone. That style also turns on the typeface's alternate letters (a
   * joined "ct", a ball-ended "r"), which the design does not use, so they
   * are turned back off here.
   */
  display:
    "[font-feature-settings:normal] text-[20px] leading-[25px] md:text-[20px] lg:text-[36px] md:leading-[25px] lg:leading-[45px] tracking-[-0.4px] md:tracking-[-0.4px] lg:tracking-[-0.36px]",
  /**
   * For a 36px heading the design draws in a box cut to the height of its
   * capitals: 27px for a line where the line itself is 45px. The gaps around
   * such a heading are measured from that box, so the 9px of line above and
   * below it is given back here and the gaps can be the design's own numbers.
   */
  displayTrim: "lg:-my-[9px]",
  /** A figure in the snapshot panel, which is 36px on a 27px line at every width */
  figure: "[font-feature-settings:normal] text-[36px] leading-[27px] md:text-[36px] md:leading-[27px]",
  /** Band quotes: 16px medium italic on a phone, 20px bold italic up */
  quote:
    "text-[16px] leading-[24px] font-medium italic md:text-[16px] lg:text-[20px] md:leading-[24px] lg:leading-[25px] md:font-medium lg:font-bold lg:tracking-[-0.4px]",
  /**
   * Headings the design sets bold on a phone and medium at full size. A
   * medium 36px heading is spaced tighter than a bold one, at -0.72px.
   */
  displayWeight: "font-bold md:font-bold lg:font-medium lg:tracking-[-0.72px]",
  /** The same tighter spacing, for a heading that is medium at every width */
  displayMedium: "font-medium lg:tracking-[-0.72px]",
} as const;

/** The pill the design uses for every call to action: 224 wide, 44 tall, 16px medium. */
export const marketingButton = {
  /** Full width on a phone, the design's 224px pill from a tablet up */
  className: "w-full h-11 px-4 lg:w-auto lg:min-w-[224px]",
  typeClassName: "text-[16px] md:text-[16px] leading-[24px] font-medium",
  /** The smaller label the design gives buttons inside a card on a phone */
  compactTypeClassName:
    "text-[14px] leading-[20px] md:text-[14px] lg:text-[16px] md:leading-[20px] lg:leading-[24px] font-medium",
} as const;

/** The 2px accent rule under a label. */
export const marketingAccent = "border-b-[3px] border-brand-600 pb-px";

/**
 * What the shared inputs do not do and the design's forms need: 44px fields at
 * 16px, the dark green border, olive placeholders, no shadow, and textareas
 * that keep their size.
 */
export const marketingFields = {
  column: [
    "w-full max-w-[476px] mx-auto",
    "[&_input]:h-11 [&_input]:px-3.5 [&_input]:text-[16px] [&_input]:border-brand-800 [&_input]:shadow-none",
    // The shared input draws its placeholder as a sibling of the field rather
    // than with the browser's own, so that is the element to size and colour.
    //
    // #606800 rather than brand-400: the hint sits on brand-50, where
    // brand-400 reaches only 2.77 against the 4.5 that 16px text needs, and
    // this hint is the first thing read on an empty form. It is the colour
    // the newsletter form already uses for the same text, at 5.75, so the
    // marketing forms now agree rather than differing by one of them
    // having been fixed.
    "[&_input+span]:left-3.5 [&_input+span]:top-[6px] [&_input+span]:text-[16px] [&_input+span]:text-[#606800]",
    "[&_input]:placeholder:text-[#606800]",
    "[&_textarea]:min-h-[102px] [&_textarea]:px-3.5 [&_textarea]:py-3 [&_textarea]:text-[16px] [&_textarea]:leading-6",
    "[&_textarea]:border-brand-800 [&_textarea]:shadow-none [&_textarea]:resize-none",
    "[&_textarea]:placeholder:text-[#606800]",
    // The design is drawn 960 tall. Where the two panels sit side by side on a
    // shorter window, the fields give up height before the page gives up
    // fitting on one screen. On a short phone screen (740px or less) a box
    // answered in starts shorter; fields keep their 44px, which a finger needs.
    "lg:[@media(max-height:900px)]:[&_input]:h-9 lg:[@media(max-height:900px)]:[&_input+span]:top-[2px] lg:[@media(max-height:900px)]:[&_textarea]:min-h-[64px] lg:[@media(max-height:900px)]:[&_textarea]:py-2",
    "lg:[@media(max-height:800px)]:[&_input]:h-8 lg:[@media(max-height:800px)]:[&_input+span]:top-0 lg:[@media(max-height:800px)]:[&_textarea]:min-h-[48px] lg:[@media(max-height:800px)]:[&_textarea]:py-1.5",
    "max-lg:[@media(max-height:740px)]:[&_textarea]:min-h-[72px] max-lg:[@media(max-height:740px)]:[&_textarea]:py-2",
  ].join(" "),
  /** The gap between fields, which closes up on a shorter desktop window or a short phone screen */
  gap: "gap-5 lg:[@media(max-height:900px)]:gap-2.5 lg:[@media(max-height:800px)]:gap-1 max-lg:[@media(max-height:740px)]:gap-3",
  /** The design marks no built-in field as required, though they still are. */
  noMark: "p-0 [&_span.text-error]:hidden",
  /** Where it does mark a question, the mark is the label's own green. */
  greenMark: "p-0 [&_span.text-error]:text-brand-700 [&_span.text-error]:ml-0",
} as const;
